import { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { useLoadStatus } from "common/load/hooks";
import { DynamicStackable } from "common/stackable/types";
import { partitionStackByType } from "common/stackable/utils";
import { HotkeyScope, useHotkeysContext } from "common/hooks/hotkeys";

import StackableContext, { StackableOpts } from "./StackableContext";
import CurrentStackableContext from "./CurrentStackableContext";

type DrawerProviderProps = { children: React.ReactNode };

type CurrentStackable = DynamicStackable & {
  absoluteIndex: number;
};

type CurrentStackableProviderProps = {
  stackable: CurrentStackable;
  remove: (id: string) => void;
  index: number;
  totalVisible: number;
  requiresManualReload: boolean;
};

const CurrentStackableProvider = (props: CurrentStackableProviderProps) => {
  const { remove, stackable, totalVisible, requiresManualReload, index } = props;

  const isFocused = !requiresManualReload && stackable.absoluteIndex >= totalVisible - 1;

  const value = useMemo(
    () => ({
      type: stackable.type,
      remove: () => remove(stackable.id),
      isFocused,
    }),
    [remove, stackable.id, stackable.type, isFocused],
  );

  return (
    <CurrentStackableContext.Provider value={value}>
      {stackable.render({
        stackIndex: index,
        isFocused,
        onClose: () => remove(stackable.id),
      })}
    </CurrentStackableContext.Provider>
  );
};

const StackableHotkeys = () => {
  const { enableScope, disableScope } = useHotkeysContext();

  useLayoutEffect(() => {
    disableScope(HotkeyScope.Page);
    enableScope(HotkeyScope.Overlay);

    return () => {
      enableScope(HotkeyScope.Page);
      disableScope(HotkeyScope.Overlay);
    };
    // Functions are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};

/**
 * Manages a stack of independently viewable items with an overlay
 * to pop the top item off the stack.
 */
const StackableProvider = ({ children }: DrawerProviderProps) => {
  const [stack, setStack] = useState<DynamicStackable[]>([]);

  // a bit of a hack to get focus trapping to play nicely with our one-off
  // confirmation dialog for expired sessions. If we need multiple of these
  // we may want to think about abstracting the current "focused" element to it's
  // own context
  const { requiresManualReload } = useLoadStatus();

  const annotatedStack = stack.map((s, i) => ({
    absoluteIndex: i, // renamed to not be over-ridden by stack-local visible indexes after partitioning below
    ...s,
  }));
  const totalVisible = annotatedStack.length;
  const stacksPartitionedByType = partitionStackByType(annotatedStack);

  // memoize stackableContextValue to avoid re-renders of all consumers of StackableContext for each
  // provider re-render
  const stackableContextValue = useMemo(() => {
    return {
      push: ({ type, render, onRemove }: StackableOpts) => {
        setStack((current) => [
          ...current,
          {
            type,
            render,
            id: uuidv4(),
            onRemove,
          },
        ]);
      },
      removeAll: (type?: string) => {
        setStack((current) => {
          const shouldRemove = (stackable: DynamicStackable) =>
            type === undefined || type === stackable.type;
          // Directly filter out stackables of the specified type
          return current.filter((stackable) => !shouldRemove(stackable));
        });
      },
    };
  }, []);

  const remove = useCallback((id: string) => {
    setStack((current) => {
      const toRemove = current.find((stackable) => stackable.id === id);

      if (toRemove) {
        toRemove.onRemove?.();
      }

      return current.filter((stackable) => stackable.id !== id);
    });
  }, []);

  return (
    <StackableContext.Provider value={stackableContextValue}>
      {totalVisible > 0 && <StackableHotkeys />}
      {children}
      {stacksPartitionedByType.flatMap((typeStack) =>
        typeStack.map((stackable, index) => (
          <CurrentStackableProvider
            key={stackable.id}
            stackable={stackable}
            remove={remove}
            index={index}
            totalVisible={totalVisible}
            requiresManualReload={requiresManualReload}
          />
        )),
      )}
    </StackableContext.Provider>
  );
};

export default StackableProvider;
